import { useEffect, useState } from "react";
import { CalendarStar, Copy, Plus, QrCode, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const STATI = { bozza: ["Bozza", "bg-zinc-100 text-zinc-600 border-zinc-300"],
  pubblicato: ["Pubblicato", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  chiuso: ["Chiuso", "bg-rose-100 text-rose-700 border-rose-300"] };
const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1";

const EMPTY = { name: "", descrizione: "", data: "", ora_da: "19:00", ora_a: "23:00",
  max_coperti: "", caparra_attiva: false, caparra_importo: "", carta_garanzia: false,
  promotion_id: "", coupon_id: "", landing_titolo: "", landing_testo: "",
  landing_cta: "Prenota il tuo tavolo", stato: "bozza" };

export default function Eventi() {
  const [items, setItems] = useState(null);
  const [promos, setPromos] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => {
    api.get("/events").then((r) => setItems(r.data)).catch(() => {});
    api.get("/promotions").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...modal,
      max_coperti: modal.max_coperti === "" ? null : parseInt(modal.max_coperti),
      caparra_importo: modal.caparra_importo === "" ? null : parseFloat(modal.caparra_importo),
      promotion_id: modal.promotion_id || null, coupon_id: modal.coupon_id || null };
    try {
      if (modal.id) await api.patch(`/events/${modal.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Evento salvato");
      setModal(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Eliminare definitivamente l'evento "${ev.name}"?`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success("Evento eliminato");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const copyLink = (ev) => {
    const url = `${window.location.origin}${ev.landing_url}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link landing copiato"));
  };

  const couponOutOfRange = !!(modal?.coupon_id && modal?.data && (() => {
    const c = coupons.find((x) => x.id === modal.coupon_id);
    return c && !(c.valid_from <= modal.data && modal.data <= (c.valid_to || "9999"));
  })());

  return (
    <div className="space-y-5 max-w-4xl" data-testid="eventi-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Eventi</h1>
          <p className="text-xs text-zinc-500">Eventi con data bloccata e landing dedicata: perfetti per le sponsorizzate.</p>
        </div>
        <button onClick={() => setModal({ ...EMPTY })} data-testid="event-new"
          className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nuovo evento
        </button>
      </div>

      {!items && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
      {items?.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-lg p-8 text-center text-sm text-zinc-400" data-testid="event-empty">
          Nessun evento. Crea il primo evento con data bloccata e condividi il link.
        </div>
      )}
      <div className="space-y-3">
        {items?.map((ev) => (
          <div key={ev.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`event-card-${ev.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-lg bg-zinc-100 text-zinc-600 flex items-center justify-center shrink-0">
                  <CalendarStar size={20} weight="bold" />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-zinc-900">{ev.name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATI[ev.stato]?.[1] || ""}`}>
                      {STATI[ev.stato]?.[0] || ev.stato}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    {new Date(ev.data + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                    {" · "}{ev.ora_da}–{ev.ora_a}
                    {ev.max_coperti && ` · max ${ev.max_coperti} coperti`}
                    {ev.caparra_attiva && ` · caparra €${ev.caparra_importo ?? ""}`}
                    {ev.carta_garanzia && " · carta a garanzia"}
                  </div>
                </div>
              </div>
              <div className="text-right text-xs text-zinc-500 shrink-0">
                <div><b className="text-zinc-900">{ev.prenotazioni}</b> prenotazioni · <b className="text-zinc-900">{ev.coperti}</b> coperti</div>
                {ev.promotion_name && <div>Promo: {ev.promotion_name}</div>}
                {ev.coupon_name && <div>Coupon: {ev.coupon_name}</div>}
              </div>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={() => copyLink(ev)} data-testid={`event-copy-${ev.id}`}
                className="h-8 px-3 rounded-md border border-zinc-300 text-xs font-semibold flex items-center gap-1.5">
                <Copy size={13} /> Copia link landing
              </button>
              {ev.stato === "pubblicato" && (
                <a href={ev.landing_url} target="_blank" rel="noreferrer" data-testid={`event-open-${ev.id}`}
                  className="h-8 px-3 rounded-md border border-zinc-300 text-xs font-semibold flex items-center gap-1.5">
                  <QrCode size={13} /> Apri landing
                </a>
              )}
              <button onClick={() => setModal({ ...EMPTY, ...ev, max_coperti: ev.max_coperti ?? "",
                caparra_importo: ev.caparra_importo ?? "", promotion_id: ev.promotion_id || "",
                coupon_id: ev.coupon_id || "" })}
                className="h-8 px-3 rounded-md border border-zinc-300 text-xs font-semibold" data-testid={`event-edit-${ev.id}`}>Modifica</button>
              <button onClick={() => remove(ev)}
                className="h-8 px-3 rounded-md border border-rose-200 text-rose-600 text-xs font-semibold" data-testid={`event-delete-${ev.id}`}>Elimina</button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setModal(null)}>
          <form onSubmit={save} className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="event-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{modal.id ? "Modifica evento" : "Nuovo evento"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div>
              <label className={labelCls}>Nome evento</label>
              <input required value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })}
                className={inputCls} data-testid="event-name" placeholder="Es. Serata con artista — Sabato" />
            </div>
            <div>
              <label className={labelCls}>Descrizione</label>
              <textarea value={modal.descrizione} onChange={(e) => setModal({ ...modal, descrizione: e.target.value })}
                rows={2} className={`${inputCls} h-auto py-2`} data-testid="event-descrizione" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Data (bloccata)</label>
                <input required type="date" value={modal.data} onChange={(e) => setModal({ ...modal, data: e.target.value })}
                  className={inputCls} data-testid="event-data" />
              </div>
              <div>
                <label className={labelCls}>Dalle</label>
                <input type="time" value={modal.ora_da} onChange={(e) => setModal({ ...modal, ora_da: e.target.value })}
                  className={inputCls} data-testid="event-ora-da" />
              </div>
              <div>
                <label className={labelCls}>Alle</label>
                <input type="time" value={modal.ora_a} onChange={(e) => setModal({ ...modal, ora_a: e.target.value })}
                  className={inputCls} data-testid="event-ora-a" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Capienza max coperti</label>
                <input type="number" min="1" value={modal.max_coperti}
                  onChange={(e) => setModal({ ...modal, max_coperti: e.target.value })}
                  className={inputCls} data-testid="event-max-coperti" placeholder="Illimitata" />
              </div>
              <div>
                <label className={labelCls}>Stato</label>
                <select value={modal.stato} onChange={(e) => setModal({ ...modal, stato: e.target.value })}
                  className={inputCls} data-testid="event-stato">
                  {Object.keys(STATI).map((k) => <option key={k} value={k}>{STATI[k][0]}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input type="checkbox" checked={modal.caparra_attiva}
                  onChange={(e) => setModal({ ...modal, caparra_attiva: e.target.checked })}
                  className="w-4 h-4 accent-zinc-900" data-testid="event-caparra" /> Caparra richiesta
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <input type="checkbox" checked={modal.carta_garanzia}
                  onChange={(e) => setModal({ ...modal, carta_garanzia: e.target.checked })}
                  className="w-4 h-4 accent-zinc-900" data-testid="event-carta" /> Carta a garanzia
              </label>
            </div>
            {modal.caparra_attiva && (
              <div>
                <label className={labelCls}>Importo caparra (€)</label>
                <input type="number" min="0" step="0.5" value={modal.caparra_importo}
                  onChange={(e) => setModal({ ...modal, caparra_importo: e.target.value })}
                  className={inputCls} data-testid="event-caparra-importo" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Promozione collegata</label>
                <select value={modal.promotion_id} onChange={(e) => setModal({ ...modal, promotion_id: e.target.value })}
                  className={inputCls} data-testid="event-promo">
                  <option value="">— Nessuna —</option>
                  {promos.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Coupon inviato a chi prenota</label>
                <select value={modal.coupon_id} onChange={(e) => setModal({ ...modal, coupon_id: e.target.value })}
                  className={inputCls} data-testid="event-coupon">
                  <option value="">— Nessuno —</option>
                  {coupons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {couponOutOfRange && (
                  <p className="text-[11px] text-amber-600 mt-1" data-testid="event-coupon-warn">
                    Attenzione: il coupon selezionato non è valido alla data dell'evento — non verrà emesso ai clienti.
                  </p>
                )}
              </div>
            </div>
            <div className="pt-2 border-t border-zinc-100 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Landing pubblica</p>
              <div>
                <label className={labelCls}>Titolo landing</label>
                <input value={modal.landing_titolo} onChange={(e) => setModal({ ...modal, landing_titolo: e.target.value })}
                  className={inputCls} data-testid="event-landing-titolo" placeholder={modal.name || "Titolo"} />
              </div>
              <div>
                <label className={labelCls}>Testo landing</label>
                <textarea value={modal.landing_testo} onChange={(e) => setModal({ ...modal, landing_testo: e.target.value })}
                  rows={2} className={`${inputCls} h-auto py-2`} data-testid="event-landing-testo" />
              </div>
              <div>
                <label className={labelCls}>Testo bottone (CTA)</label>
                <input value={modal.landing_cta} onChange={(e) => setModal({ ...modal, landing_cta: e.target.value })}
                  className={inputCls} data-testid="event-landing-cta" />
              </div>
            </div>
            <button className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="event-save">
              Salva evento
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
